import { useEffect, useRef, useState } from "react";
import { useFormState, useWatch } from "react-hook-form";
import debounce from "lodash/debounce";
import cloneDeep from "lodash/cloneDeep";
// Eliminamos react-hot-toast
import useAlert from "./useAlert";
import { IEventModal } from "../base/modal/models/modal.types";

// Campos que serán excluidos del borrador
const EXCLUDED_FIELDS: string[] = [];

interface UseAutoDraftOptions {
  // Función para guardar el borrador
  onSave: (data: any) => Promise<void>;
  // Función opcional para limpiar el borrador
  onCancel?: () => Promise<void>;
  // Tiempo de espera entre guardados (ms)
  debounceTime?: number;
  // Campos adicionales a excluir
  excludeFields?: string[];
  // Modo de edición o creación
  isCreating: boolean;
  // Función opcional para verificar si existe un borrador
  checkDraft?: () => Promise<any | null>;
  // Función opcional para aplicar el borrador
  applyDraft?: (data: any) => void;
  // Callbacks opcionales para inicio/fin de procesos
  onProcessStart?: (state: "saving" | "canceling" | "checking") => void;
  onProcessEnd?: (state: "saving" | "canceling" | "checking") => void;
}

interface DraftState {
  loading: boolean;
  canceling: boolean;
  error: boolean;
  checkingDraft: boolean;
}

export const useAutoDraft = (form: any, options: UseAutoDraftOptions) => {
  const {
    onSave,
    onCancel,
    debounceTime = 2000,
    excludeFields = [],
    isCreating,
    checkDraft,
    applyDraft,
    onProcessStart,
    onProcessEnd
  } = options;

  const formState = useFormState({ control: form.control });
  const watchedData = useWatch({ control: form.control });
  const previousValues = useRef<any>(null);
  const [state, setState] = useState<DraftState>({
    loading: false,
    canceling: false,
    error: false,
    checkingDraft: false
  });

  const { infoAlert } = useAlert();

  // Función para limpiar los campos excluidos
  const cleanExcludedFields = (data: any) => {
    const excludedList = [...EXCLUDED_FIELDS, ...excludeFields];
    const cleanData = cloneDeep(data);

    const removeExcludedFields = (obj: any) => {
      for (const key in obj) {
        if (excludedList.includes(key)) {
          delete obj[key];
          continue;
        }

        if (obj[key] && typeof obj[key] === "object") {
          removeExcludedFields(obj[key]);
        }
      }
    };

    removeExcludedFields(cleanData);
    return cleanData;
  };

  // Función para guardar el borrador
  const saveDraft = debounce(async (data: any) => {
    if (state.checkingDraft) return;
    if (!isCreating) return;

    const cleanData = cleanExcludedFields(data);

    if (
      previousValues.current &&
      JSON.stringify(cleanData) === JSON.stringify(previousValues.current)
    ) {
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: false }));
      onProcessStart?.("saving");

      await onSave(cleanData);
      previousValues.current = cleanData;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: true
      }));
    } finally {
      setState(prev => ({ ...prev, loading: false }));
      onProcessEnd?.("saving");
    }
  }, debounceTime);

  const cancelDraft = async () => {
    try {
      setState(prev => ({ ...prev, canceling: true, error: false }));
      onProcessStart?.("canceling");

      if (onCancel) {
        await onCancel();
      }
      previousValues.current = null;
      saveDraft.cancel();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: true
      }));
    } finally {
      setState(prev => ({ ...prev, canceling: false }));
      onProcessEnd?.("canceling");
    }
  };

  const verifyDraft = debounce(async () => {
    if (!checkDraft) return;
    setState(prev => ({ ...prev, checkingDraft: true }));
    onProcessStart?.("checking");

    try {
      const draft = await checkDraft();
      if (draft) {
        await infoAlert({
          message:
            "Se encontró un borrador previo. ¿Deseas aplicarlo o descartarlo?",
          confirmBtnMessage: "Aplicar",
          declineBtnMessage: "Descartar"
        }).then(result => {
          if (result.type.confirm) {
            applyDraft?.(draft);
          } else if (result.type.close) {
            cancelDraft();
          }
        });
      }
    } catch (error) {
      console.error("Error al verificar borrador:", error);
    } finally {
      setState(prev => ({ ...prev, checkingDraft: false }));
      onProcessEnd?.("checking");
    }
  });

  // Efecto para comprobar borrador existente al montar
  useEffect(() => {
    verifyDraft();
    return () => verifyDraft.cancel();
  }, []);

  useEffect(() => {
    if (formState.isDirty) {
      saveDraft(watchedData);
    }
    return () => saveDraft.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedData]);

  return {
    isDraftSaving: state.loading,
    isDraftCanceling: state.canceling,
    isDraftError: state.error,
    isCheckingDraft: state.checkingDraft,
    cancelDraft
  };
};
